import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import { DialogProps as MUIDialogProps } from "@mui/material/Dialog";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog, } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import CreatorLink from "app/routes/proposals/creator-link";
import ProposalToolbar from "app/routes/proposals/proposal-toolbar";
import { PageTab } from "app/routes/proposals/tabs";
import { DialogProps } from "common/components/dialog";
import { NotAuthorized } from "common/components/not-authorized";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
  type MRT_Cell as Cell,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSession } from "users/session/session-context";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal
} from "work/entities/comment-thread/store/comment-thread-redux-slice";
import {
  clearUnsavedCommentsByProposalId,
  getAreUnsavedComments,
  saveDraftComments
} from "work/entities/comment/store/comments-redux-slice";
import ProposalAPIService, { ProposalUpdateError } from "work/entities/proposal/api/proposal-api-service";
import ProposalParameters, {
  ProposalBoxType,
  ProposalContext,
  ProposalFilterParameters,
  ProposalOrderField,
  ProposalOrderParameters,
} from "work/entities/proposal/api/request-contracts/proposal-parameters";
import ProposalArchiveDialog from "work/entities/proposal/archive/view/proposal-archive-dialog";
import ProposalDraftDialog from "work/entities/proposal/draft/view/proposal-draft-dialog";
import ProposalRequestDraftDialog from "work/entities/proposal/draft/view/proposal-request-draft-dialog";
import Proposal, { ProposalFieldCategory, } from "work/entities/proposal/proposal";
import { default as ProposalRedline } from "work/entities/proposal/redlining/view/proposal-redline-dialog";
import {
  addProposal,
  clearNewProposalBuilder,
  getActiveProposalCount,
  getArchivedProposalCount,
  getDraftProposalCount,
  getErrorLoadingProposalById,
  getErrorLoadingProposalCounts,
  getErrorLoadingProposalsByQuery,
  getIsLoadingProposalById,
  getIsLoadingProposalCounts,
  getIsLoadingProposalsByQuery,
  getProposalBuilderByProposalId,
  getProposalById,
  getProposalQueryByInterface,
  getProposalsByQuery,
  populateProposal,
  populateProposalCounts,
  populateProposalsByQuery,
  ProposalQuery,
  removeProposal,
  replaceProposalBuilder,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery
} from "work/entities/proposal/store/proposals-redux-slice";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";
import WorkAgreement, { Masked } from "work/entities/work-agreement/work-agreement";
import { HumanReadableProposalStatus, ProposalStatus, } from "work/values/constants";
import FeeScheduleLink from "work/values/fee-schedule/view/fee-schedule-link";
import ProposalIssues from "work/values/proposal-issues/proposal-issues";
import ProposalReviewer from "work/values/proposal-reviewer";
import SelectedRepresentative from "work/values/selected-representative/selected-representative";
import SelectedVendorTeam from "work/values/selected-vendor-team/selected-vendor-team";
import VendorTeamLink from "work/values/vendor-team/view/vendor-team-link";

const ProposalToolbarButtonsContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
    },
    display: "flex",
    alignItems: "end",
    flexDirection: "column",
    gap: theme.spacing(1),
  }));
const ProposalToolbarButtons = styled("div")(
  () => ({
    display: "flex",
    alignItems: "end",
    flexWrap: "nowrap",
  }));
const ProposalAlertActions = styled("div")(
  ({ theme }) => ({
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "nowrap",
    height: "100%",
    paddingRight: theme.spacing(2),
    whiteSpace: "nowrap",
  }));
const RowTitle = styled(Typography)(
  () => ({
    fontSize: "1.1em",
    fontWeight: "600",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const NewButton = styled(IconButton)(
  () => ({
    padding: 0,
  }));

const NameCell = ({ row }: { row: { original: Proposal } }) =>
  <RowTitle>{row.original.name?.value}</RowTitle>;

const NegotiableCell = ({ cell }: { cell: Cell<Proposal, boolean> }) =>
  cell.getValue() ? <CheckIcon /> : <CloseIcon />;

export default function Proposals() {
  const columnHelper = createMRTColumnHelper<Proposal>();
  const [activeTab, setActiveTab] = React.useState(PageTab.Active);

  const { numDialogs, openDialog, closeDialog, updateDialogProps } = useDialog();
  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const dispatch = useAttorneyHubDispatch();
  const activeProposalCount = getActiveProposalCount();
  const draftProposalCount = getDraftProposalCount();
  const archivedProposalCount = getArchivedProposalCount();
  const isLoadingProposalCounts = getIsLoadingProposalCounts();
  const errorLoadingProposalCounts = getErrorLoadingProposalCounts();
  

  const proposalQuery = getProposalQueryByInterface("proposalsIndex");
  const paginatedProposals = getProposalsByQuery(proposalQuery);
  const isLoadingQueryFromStore = getIsLoadingProposalsByQuery(proposalQuery);
  const errorLoadingQuery = getErrorLoadingProposalsByQuery(proposalQuery);

  const proposalId = routeParams.id ? new Guid(routeParams.id) : undefined;
  const dialogProposal = getProposalById(proposalId);
  const isLoadingProposal = getIsLoadingProposalById(proposalId);
  const errorLoadingProposal = getErrorLoadingProposalById(proposalId);

  const commentThreads = getCommentThreadsByProposal(dialogProposal);
  const isLoadingCommentThreads = getIsLoadingCommentThreadsByProposal(
    undefined,
    proposalId
  );
  const errorLoadingCommentThreads = getErrorLoadingCommentThreadsByProposal(
    undefined,
    proposalId
  );

  const [isLoading, setIsLoading] = React.useState(isLoadingQueryFromStore ?? false);
  const [rowSelection, setRowSelection] = React.useState<
    Record<string, boolean>
  >({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<Array<{ id: string, desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const [rowCount, setRowCount] = useState(0);

  const proposalBuilder = getProposalBuilderByProposalId(proposalId ?? Guid.empty);
  const areUnsavedComments = getAreUnsavedComments(dialogProposal);
  const [activeDialogId, setActiveDialogId] = React.useState<Guid | undefined>();
  const [activeDialogProps, setActiveDialogProps] = React.useState<DialogProps>();

  const [isSaving, setIsSaving] = React.useState(false);

  useEffect(
    () => {
      if (
        !activeDialogId ||
        !activeDialogProps ||
        !session?.user?.id ||
        dialogProposal?.isSubmitted ||
        dialogProposal?.supersedes
      ) {
        return;
      }
      let disableSaveButton = false;
      let tooltip = "Save changes (just for yourself)";
      if (!proposalBuilder) {
        disableSaveButton = true;
        tooltip = "Fill out required fields to save.";
      } else if (!proposalBuilder.isModified &&
        !dialogProposal?.hasUnsavedRedlineChanges &&
        !areUnsavedComments) {
        disableSaveButton = true;
        tooltip = "No changes to save";
      }

      const issues = ProposalIssues.fromBuilder(proposalBuilder);
      if (!issues.canSave) {
        disableSaveButton = true;
        tooltip = "Fill out required fields to save.";
      }

      const newDialogProps = activeDialogProps;
      newDialogProps.saveAbleItem = proposalBuilder;
      newDialogProps.disableSave = disableSaveButton;
      newDialogProps.saveButtonTooltip = tooltip;
      newDialogProps.isDirty = proposalBuilder?.isModified || areUnsavedComments
      newDialogProps.onSave = async () => {
        if (!proposalBuilder) {
          console.error("Nothing to save.")
          return;
        }

        await handleSaveDraft(
          proposalBuilder,
          Boolean(proposalId)
        )
      }

      updateDialogProps(
        activeDialogId,
        newDialogProps
      );
      setActiveDialogProps(newDialogProps)
    },
    [
      dialogProposal,
      dialogProposal?.reviewersKey,
      proposalId?.value,
      proposalBuilder,
      areUnsavedComments
    ]
  );

  useEffect(
    () => {
      if (!dialogProposal?.redline || !activeDialogId || !activeDialogProps) {
        return;
      }
      let disableSaveButton = false;
      let tooltip = "Save changes (just for yourself)";
      const isModified = dialogProposal.isModified || areUnsavedComments;
      if (!isModified) {
        disableSaveButton = true;
        tooltip = "No changes to save";
      }

      const newDialogProps = activeDialogProps;
      newDialogProps.saveAbleItem = dialogProposal;
      newDialogProps.disableSave = disableSaveButton;
      newDialogProps.saveButtonTooltip = tooltip;
      newDialogProps.isDirty = isModified;
      updateDialogProps(
        activeDialogId,
        newDialogProps
      );
      setActiveDialogProps(newDialogProps)
    },
    [
      dialogProposal,
      dialogProposal?.reviewersKey,
      areUnsavedComments
    ]
  );

  useEffect(
    () => {
      if (routeParams.pageTab) {
        setActiveTab(routeParams.pageTab as PageTab);
      } else {
        navigate(`/proposals/${PageTab.Active}`);
      }
    },
    [routeParams]
  );

  useEffect(
    () => {
      dispatchProposalQuery();
    },
    [
      activeTab,
      routeParams,
      searchParams
    ]
  );


  useEffect(
    () => {
      const newSearchParams = new URLSearchParams();
      if (globalFilter) {
        newSearchParams.set("query", globalFilter);
      }

      const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value as string | undefined;
      if (statusFilter) {
        newSearchParams.set("status", statusFilter);
      }

      const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
      if (nameFilter) {
        newSearchParams.set("name", nameFilter);
      }

      const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
      if (descriptionFilter) {
        newSearchParams.set("description", descriptionFilter);
      }

      const creatorFilter = columnFilters.find((filter) => filter.id === "creator")?.value as string | undefined;
      if (creatorFilter) {
        newSearchParams.set("creator", creatorFilter);
      }

      const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
      if (negotiableFilter !== undefined) {
        newSearchParams.set("negotiable", negotiableFilter);
      }

      const orderingField = sorting.find((sort) =>
        Object.values(ProposalOrderField)
          .map((field) => field.toLowerCase())
          .includes(sort.id.toLowerCase()));
      
      if (orderingField) {
        newSearchParams.set("orderBy", orderingField.id);
        newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending)
      }
      
      const paginationPageIndex = pagination.pageIndex ?? 0;
      newSearchParams.set("page", (paginationPageIndex + 1).toString());
      const paginationPageSize = pagination.pageSize ?? 10;
      newSearchParams.set("pageSize", paginationPageSize.toString());
            
      setSearchParams(newSearchParams);
    },
    [
      globalFilter,
      columnFilters,
      sorting,
      pagination
    ]
  );

  useEffect(
    () => {
      if (errorLoadingQuery) {
        enqueueSnackbar(
          "Error loading proposal table",
          { variant: "error" }
        );
      }
      if (dialogProposal === null) {
        if (routeParams.action === 'review' && routeParams.id) {
          enqueueSnackbar(
            "You are no longer a reviewer on this proposal",
            { variant: "warning" }
          )
        } else {
          enqueueSnackbar(
            "Proposal not found",
            { variant: "warning" }
          );
        }
        navigate(`/proposals/${activeTab}`);
      } else if (errorLoadingProposal) {
        enqueueSnackbar(
          "Error loading proposal",
          { variant: "error" }
        );
        navigate(`/proposals/${activeTab}`);
      }
    },
    [
      errorLoadingQuery,
      errorLoadingProposal,
      dialogProposal
    ]
  );

  useEffect(
    () => {
      if (session.user?.id && proposalId && dialogProposal) {
        const pageTab = routeParams.pageTab as PageTab;
        const action = routeParams.action as string;
        const reviewer = dialogProposal.getReviewer(session.user.id);

        redirectForProposalIfNecessary(
          action,
          reviewer,
          pageTab
        );
      }
    },
    [
      routeParams,
      proposalId?.value,
      dialogProposal?.id?.value,
      dialogProposal?.replacedBy?.value,
      dialogProposal?.status,
      dialogProposal?.reviewersKey,
      dialogProposal?.isSubmitted
    ]
  );

  useEffect(
    () => {
      if (!session.user) return;
      if (
        proposalId &&
        dialogProposal === undefined &&
        !isLoadingProposal &&
        !errorLoadingProposal
      ) {
        dispatch(
          populateProposal({
            session: session,
            id: proposalId,
          })
        );
      } else if (!(proposalId && !dialogProposal)) {
        const tab =
          ProposalFieldCategory[
          routeParams.tab as keyof typeof ProposalFieldCategory
          ];
        const action = routeParams.action as string;
        if (!action) return;
        if (numDialogs === 0) {
          openProposalDialog(
            action,
            tab
          );
        }
      }

      if (
        proposalId &&
        commentThreads === undefined &&
        !isLoadingCommentThreads &&
        !errorLoadingCommentThreads
      ) {
        dispatch(
          populateCommentThreadsByProposal({
            session: session,
            proposalId: proposalId,
          })
        );
      }
    },
    [
      proposalId?.value,
      dialogProposal,
      dialogProposal?.id?.value,
      isLoadingProposal,
      routeParams,
      dialogProposal?.reviewersKey
    ]
  );

  useEffect(
    () => {
      if (
        proposalQuery &&
        !isLoadingQueryFromStore &&
        paginatedProposals === undefined
      ) {
        dispatch(
          populateProposalsByQuery({
            session: session,
            query: proposalQuery,
          })
        );
      }
      else if (paginatedProposals) {
        setRowCount(paginatedProposals.totalElements);
      }
    },
    [
      proposalQuery,
      paginatedProposals
    ]
  );

  useEffect(
    () => {
      setIsLoading(isLoadingQueryFromStore);
    },
    [isLoadingQueryFromStore]
  );

  useEffect(() => {
    if (isLoadingProposalCounts || errorLoadingProposalCounts || !session) return;

    if (
      activeProposalCount === undefined &&
      draftProposalCount === undefined &&
      archivedProposalCount === undefined
    )
      dispatch(populateProposalCounts({ session: session }));
  }, [
    isLoadingProposalCounts,
    errorLoadingProposalCounts,
    activeProposalCount,
    draftProposalCount,
    archivedProposalCount,
    session
  ]);

  function redirectForProposalIfNecessary(
    action: string,
    reviewer: ProposalReviewer | undefined,
    pageTab: PageTab
  ) {
    if (!dialogProposal) return;
    let redirectAction: string | undefined = undefined;

    // If the proposal was replaced by another proposal, redirect to the new proposal
    if (dialogProposal.replacedBy) {
      navigate(
        `/proposals/${pageTab}/${action}/${dialogProposal.replacedBy.value}`,
        {
          replace: true,
          state: {
            previousProposalId: dialogProposal.id?.value,
            newProposalId: dialogProposal.replacedBy.value
          }
        },
      );
      closeDialog();
      return;
    }

    // If the proposal hired, redirect to the work agreement
    if (dialogProposal.isHired && activeTab !== PageTab.Archived) {
      navigate(
        `/work/active/view/${dialogProposal.workAgreementId?.value}`,
        {
          replace: true
        },
      );
      return;
    }

    // If the proposal is submitted, redirect to the active proposals tab
    if (action === "edit" && dialogProposal.isSubmitted) {
      navigate('/proposals/active', { replace: true });
      return;
    }

    if (!dialogProposal.isArchived && reviewer && action !== "review") {
      redirectAction = "review";
    }
    if (dialogProposal.isArchived && action !== "view") {
      redirectAction = "view";
    }

    let redirectPageTab: PageTab | undefined = undefined;
    if (pageTab === PageTab.Drafts && dialogProposal.isSubmitted) {
      redirectPageTab = PageTab.Active;
    }
    if (pageTab === PageTab.Active && !dialogProposal.isSubmitted) {
      redirectPageTab = PageTab.Drafts;
    }
    if (dialogProposal.isArchived && pageTab !== PageTab.Archived) {
      redirectPageTab = PageTab.Archived;
    }

    if (redirectAction || redirectPageTab) {
      const newPath = `/proposals/${redirectPageTab ?? pageTab}/${redirectAction ?? action}/${proposalId}`;
      navigate(
        newPath,
        { replace: true }
      );
    }
  }

  function openProposalDialog(action: string, _tab: ProposalFieldCategory) {
    if (!session.user || dialogProposal?.replacedBy || !action) {
      console.warn("Unable to open proposal dialog. Missing required parameters.");
      return;
    }
    switch (action) {
      case "create":
        handleCreateDraftProposal();
        break;
      case "request":
        handleRequestProposal();
        break;
      case "edit":
        handleOpenDraftProposalDialog(
          "Edit Draft Proposal",
          proposalId
        );
        break;
      case "review":
        if (proposalId && dialogProposal?.isSubmitted) {
          handleOpenActiveProposalDialog(
            "Review Proposal",
            proposalId
          )
        } else if (dialogProposal) {
          handleOpenDraftProposalDialog(
            "Review Draft Proposal",
            proposalId
          )
        }
        break;
      case "revise":
        if (proposalId) {
          handleOpenActiveProposalDialog(
            "Revise Proposal",
            proposalId
          );
        }
        break;
      default:
        if (dialogProposal?.isArchived && proposalId) {
          handleOpenArchivedProposalDialog(proposalId);
        } else if (proposalId && dialogProposal?.isSubmitted) {
          handleOpenActiveProposalDialog(
            "View Proposal",
            proposalId,
            true
          )
        } else if (dialogProposal) {
          handleOpenDraftProposalDialog(
            "View Draft Proposal",
            proposalId,
            true
          )
        }
        break;
    }
  }

  function dispatchProposalQuery() {
    if (!session.user?.companyEntityId) return;

    let boxType = searchParams.get("boxType") as ProposalBoxType | undefined;
    if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Active)
      boxType = ProposalBoxType.AllActive
    else if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Drafts)
      boxType = ProposalBoxType.Draft
    else if (!boxType &&
      (routeParams.pageTab ?? activeTab) === PageTab.Archived)
      boxType = ProposalBoxType.Archive


    const filterParams = new ProposalFilterParameters();

    const queryFilter = searchParams.get("query");
    if (queryFilter) {
      filterParams.query = queryFilter;
    }

    const statusFilter = searchParams.get("status");
    if (statusFilter) {
      filterParams.status = ProposalStatus[statusFilter as keyof typeof ProposalStatus];
    }

    const nameFilter = searchParams.get("name");
    if (nameFilter) {
      filterParams.name = nameFilter;
    }

    const negotiableFilter = searchParams.get("negotiable");
    if (negotiableFilter) {
      filterParams.isNegotiable = negotiableFilter === "true";
    }

    if (session.context?.viewingAsVendor) {
      filterParams.context = ProposalContext.Vendor;
    } else {
      filterParams.context = ProposalContext.Client;
    }
    if (boxType !== undefined) {
      filterParams.boxType = boxType;
    }
    const orderParams = new ProposalOrderParameters(
      ProposalOrderField.Created,
      OrderDirection.Descending
    );

    const orderByParam = searchParams.get("orderBy");
    const orderDirParam = searchParams.get("orderDir");
    if (orderByParam) {
      const enumValue = Object.values(ProposalOrderField).find(
        (field) => field.toLowerCase() === orderByParam.toLowerCase()
      );
      if (enumValue) {
        orderParams.orderBy = enumValue;
      }
    }
    if (orderDirParam) {
      const enumValue = Object.values(OrderDirection).find(
        (field) => field.toLowerCase() === orderDirParam.toLowerCase()
      );
      if (enumValue) {
        orderParams.orderDir = enumValue;
      }
    }

    let pageIndex = 0;
    let pageSize = 10;
    const pageParam = searchParams.get("page");
    if (pageParam) {
      const parsed = Number.parseInt(pageParam);
      if (!Number.isNaN(parsed) && parsed > 0) {
        pageIndex = parsed - 1;
      }
    }
    const pageSizeParam = searchParams.get("pageSize");
    if (pageSizeParam) {
      const parsed = Number.parseInt(pageSizeParam);
      if (!Number.isNaN(parsed) && parsed > 0 && parsed <= 100) {
        pageSize = parsed;
      }
    }
    const paginationParams = new PaginationParameters(pageIndex, pageSize);

    const proposalParams = new ProposalParameters(
      filterParams,
      orderParams
    );

    const newQuery = new ProposalQuery(proposalParams, paginationParams);
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: "proposalsIndex",
        query: newQuery,
      })
    );
  }

  function handleOpenArchivedProposalDialog(proposalId: Guid) {
    const parentPath = new URL(
      "..",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: "Archived Proposal",
        MuiProps: { fullWidth: false, maxWidth: "lg" },
        contentSxProps: { pb: { xs: 0 } },
        titleStyle: { paddingBottom: 0 },
        resetStack: true,
        component: (
          <ProposalArchiveDialog
            navigate={navigate}
            proposalId={proposalId}
          />
        ),
      },
      () => navigate(parentPath.pathname)
    );
  }

  function handleRequestProposal() {
    const preSelectedRep =
      SelectedRepresentative.fromURLSearchParams(searchParams);
    const preSelectedTeam = SelectedVendorTeam.fromURLSearchParams(searchParams);
    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: `Request Proposal from ${preSelectedRep?.name ?? preSelectedTeam?.targetTeamName ?? "Vendor"
          }`,
        resetStack: true,
        component: (
          <ProposalRequestDraftDialog
            onProposalSubmitted={(submittedProposal: Proposal) => {
              closeDialog();
              dispatch(addProposal(submittedProposal));
              navigate(
                `/proposals/active/revise/${submittedProposal.id}`,
                {
                  replace: true,
                }
              );
            }}
            onCancel={() => {
              closeDialog();
              navigate(parentPath.pathname);
            }}
            preSelectedRep={preSelectedRep}
            preSelectedTeam={preSelectedTeam}
          />
        ),
      },
      () => navigate(parentPath.pathname)
    );
  }

  function handleCreateDraftProposal() {
    const preSelectedRep =
      SelectedRepresentative.fromURLSearchParams(searchParams);
    const preSelectedTeam = SelectedVendorTeam.fromURLSearchParams(searchParams);
    const existingWorkAgreementJSON = location.state?.workAgreement;
    const existingWorkAgreement = existingWorkAgreementJSON
      ? WorkAgreement.fromJSON(new WorkAgreementAPIService(session), existingWorkAgreementJSON)
      : undefined;

    const draftDialogProps: DialogProps = {
      title: "Create Proposal",
      MuiProps: {
        fullWidth: false,
        maxWidth: "lg"
      } as Partial<MUIDialogProps>,
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      resetStack: true,
      onClose: handleCloseDraftDialog,
      isDirty: false,
      showSaveButton: true,
      disableSave: true,
      onSave: (saveAbleItem?: unknown, preventNavigation?: boolean) => {
        if (saveAbleItem && saveAbleItem instanceof ProposalBuilder) {
          return handleSaveDraft(saveAbleItem, preventNavigation);
        }
        throw new Error("Invalid proposal builder");
      },
      component: (
        <ProposalDraftDialog
          navigate={navigate}
          isSaving={isSaving}
          onSaveCallback={handleSaveDraft}
          onProposalSubmitted={() => {
            closeDialog(handleCloseDraftDialog);
          }}
          preSelectedRep={preSelectedRep}
          preSelectedTeam={preSelectedTeam}
          existingWorkAgreement={existingWorkAgreement}
        />
      ),
    };

    setActiveDialogProps(draftDialogProps);
    setActiveDialogId(openDialog(
      draftDialogProps,
      handleCloseDraftDialog
    ));
  }

  function handleOpenDraftProposalDialog(
    title: string,
    proposalId?: Guid,
    disableEditing?: boolean
  ) {
    if (!proposalId) {
      console.error("Proposal ID is required to edit a draft proposal.");
      return;
    }

    const draftDialogProps: DialogProps = {
      title: title,
      MuiProps: { fullWidth: false, maxWidth: "lg" },
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      resetStack: true,
      onClose: handleCloseDraftDialog,
      isDirty: false,
      showSaveButton: true,
      disableSave: true,
      onSave: async () => {
        if (!proposalBuilder) {
          console.error("Nothing to save.")
          return;
        }

        await handleSaveDraft(
          proposalBuilder,
          Boolean(proposalId)
        )
      },
      component: (
        <ProposalDraftDialog
          navigate={navigate}
          proposalId={proposalId}
          isSaving={isSaving}
          onProposalSubmitted={() => {
            closeDialog(handleCloseDraftDialog);
          }}
          disableEditing={disableEditing}
        />
      ),
    }
    setActiveDialogProps(draftDialogProps);
    setActiveDialogId(openDialog(
      draftDialogProps,
      handleCloseDraftDialog
    ));
  }

  const handleCloseDraftDialog = () => {
    if (proposalId) {
      dispatch(clearUnsavedCommentsByProposalId(proposalId));

      dispatch(resetBuilderByProposalId(proposalId));
    } else {
      dispatch(clearNewProposalBuilder());
    }

    navigate(
      '/proposals/drafts',
      { replace: true }
    );
    setActiveDialogId(undefined);
    setActiveDialogProps(undefined);
  };

  const handleSaveDraft = async (
    builder: ProposalBuilder,
    preventNavigation?: boolean
  ) => {
    if (!builder) {
      throw new Error("Proposal builder is required to save a proposal.");
    }

    const newProposal = await saveProposal(builder);
    if (proposalId) {
      dispatch(saveDraftComments(session));
    }
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: "proposalsIndex",
        query: proposalQuery,
      })
    );
    let url = `/proposals/drafts`;
    if (
      !preventNavigation &&
      newProposal &&
      !location.pathname.includes('/proposal/drafts/edit/')
    ) {
      closeDialog();
      setActiveDialogId(undefined);
      setActiveDialogProps(undefined);
      url = `/proposals/drafts/edit/${newProposal.id}`;
      navigate(url);
    }
    enqueueSnackbar(
      "Changes saved",
      { variant: "success" }
    );
    return newProposal;
  }

  const handleSaveRedlinedProposal = async (
    proposal: Proposal
  ) => {
    if (!proposal?.redline) {
      console.error("No proposal to save");
      return;
    }
    try {
      setIsSaving(true);
      dispatch(saveDraftComments(session));
      proposal.redline.clearSessionHistory();
      const {
        proposal: savedProposal,
        userRedlining: updatedRedline
      } = await proposal.save(session);


      if (updatedRedline) {
        const proposalToAdd = savedProposal.updateRedline(updatedRedline);
        proposalToAdd.updateLastSavedRedline();
        dispatch(addProposal(proposalToAdd));
      } else {
        dispatch(addProposal(savedProposal));
      }

      enqueueSnackbar(
        "Changes saved",
        { variant: "success" }
      );

      if (proposal.areReviewersModified) {
        enqueueSnackbar(
          "Reviewers have been updated/invited",
          { variant: "success" }
        )
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to save proposal",
        { variant: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  }

  const saveProposal = async (
    proposalBuilder: ProposalBuilder,
  ): Promise<Proposal | undefined> => {
    if (!proposalBuilder) {
      enqueueSnackbar(
        "Failed to save draft",
        { variant: "error" }
      );
      console.error(
        "Failed to save proposal draft: no proposalBuilder"
      );
      return;
    }
    setIsSaving(true);
    try {
      let unsavedDraftProposal: Proposal;
      if (!proposalId) {
        unsavedDraftProposal = proposalBuilder.buildNewDraft(session);
      } else if (!dialogProposal) {
        enqueueSnackbar(
          "Failed to save draft",
          { variant: "error" }
        );
        console.error(
          "Failed to save proposal draft: no dialogProposal"
        );
        return;
      } else {
        unsavedDraftProposal = proposalBuilder.updateDraftProposal(
          dialogProposal,
          session
        );
      }
      proposalBuilder.setLastSavedIndex();
      if (dialogProposal?.id) {
        dispatch(
          replaceProposalBuilder({
            proposalId: dialogProposal.id,
            builder: proposalBuilder.clone(),
          })
        );  
      }
      
      const { proposal: savedProposal } =
        await unsavedDraftProposal.save(
        session,
        proposalBuilder.toJSON()
      );

      // if (dialogProposal?.id && userProposalBuilder) {
      //   dispatch(updateProposal(savedProposal));
      //   dispatch(replaceProposalBuilder({ proposalId: dialogProposal.id, builder: userProposalBuilder }));
      // } else {
      //   dispatch(addProposal(savedProposal));
      //   dispatch(clearNewProposalBuilder());        
      // }
      return savedProposal;
    } catch (error) {
      if (error instanceof ProposalUpdateError) {
        enqueueSnackbar(
          error.message,
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(
          "Failed to save draft",
          { variant: "error" }
        );
      }
      console.error(
        "Failed to save proposal draft: ",
        error
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmitProposal(proposal: Proposal) {
    if (!proposal.id)
      throw new Error("Proposal ID is required");

    const response = await confirm({
      title: "Submit Proposal",
      message: "Are you use you want to submit this proposal?",
      okButtonText: "Submit",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLoading(true);
      await proposal.submit(session.user?.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleApproveProposal(proposal: Proposal) {
    if (!proposal.id) {
      throw new Error("Proposal ID is required");
    }
    if (!session.user?.id) {
      throw new Error("User ID is required");
    }

    const response = await confirm({
      title: "Approve Proposal",
      message: "Are you use you want to approve this proposal?",
      okButtonText: "Approve",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLoading(true);
      await proposal.approve(session.user.id, session);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRejectProposal(proposal: Proposal) {
    if (!proposal.id)
      throw new Error("Proposal ID is required");

    const response = await confirm({
      title: "Reject Proposal",
      message: "Are you use you want to reject this proposal?",
      okButtonText: "Reject",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLoading(true);
      await proposal.reject(session.user?.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleHireProposal(proposal: Proposal) {
    if (!proposal.id)
      throw new Error("Proposal ID is required");

    const response = await confirm({
      title: "Hire Proposal",
      message: "Are you sure you want to hire this proposal?",
      okButtonText: "Hire",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLoading(true);
      await proposal.hire(session.user?.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteProposal(proposal: Proposal) {
    if (!proposal.id)
      throw new Error("Proposal ID is required");

    const response = await confirm({
      title: "Delete Proposal",
      message: "Are you use you want to delete this proposal?",
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await proposal.delete(session.user?.id);
      dispatch(removeProposal(proposal.id));
      dispatch(
        updateProposalInterfaceQuery({
          interfaceName: "proposalsIndex",
          query: proposalQuery,
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  async function handleCancelProposal(proposal: Proposal) {
    if (!proposal.id)
      throw new Error("Proposal ID is required");

    const response = await confirm({
      title: "Cancel Proposal",
      message: "Are you use you want to cancel this proposal?",
      okButtonText: "Ok",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await proposal.cancel(session.user?.id);
    } catch (error) {
      console.error(error);
    }
  }

  function handleOpenActiveProposalDialog(
    title: string,
    proposalId: Guid,
    readOnly?: boolean
  ) {
    const parentPath = new URL(
      "..",
      globalThis.location.origin + globalThis.location.pathname
    );
    const handleClose = () => {
      dispatch(resetProposalSessionByProposalId(proposalId));
      dispatch(clearUnsavedCommentsByProposalId(proposalId));
      closeDialog();
      navigate(parentPath.pathname ?? '/proposals/active');
    };

    const redlineDialogProps: DialogProps = {
      title: title,
      titleStyle: { paddingBottom: 0 },
      MuiProps: { fullWidth: false, maxWidth: "xl" },
      contentSxProps: { pb: { xs: 0 } },
      resetStack: true,
      onClose: handleClose,
      showSaveButton: true,
      disableSave: true,
      onSave: async (saveAbleItem?: unknown, _preventNavigation?: boolean) => {
        if (saveAbleItem && saveAbleItem instanceof Proposal) {
          return handleSaveRedlinedProposal(saveAbleItem);
        }
        throw new Error("Invalid proposal to save");
      },
      component: (
        <ProposalRedline
          proposalId={proposalId}
          navigate={navigate}
          disableEditing={readOnly ?? false}
        />
      ),
    }

    setActiveDialogProps(redlineDialogProps);
    setActiveDialogId(openDialog(
      redlineDialogProps,
      handleClose,
    ));
  }

  async function handleBulkDraftProposalDelete() {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIds.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setRowSelection({});
      const workAgreementService = new WorkAgreementAPIService(session);
      const proposalService = new ProposalAPIService(session, workAgreementService);
      await proposalService.removeDraftProposals(selectedRowIds);
      for (const id of selectedRowIds) dispatch(removeProposal(id));
      dispatch(
        updateProposalInterfaceQuery({
          interfaceName: "proposalsIndex",
          query: proposalQuery,
        })
      );
    } catch (error) {
      console.error(error);
    }
  }

  function renderStatusColumn(proposal: Proposal): string {
    const userId = session.user?.id;
    let proposalStatus: string = ProposalStatus[proposal.status].toString();
    let displayStatus: string =
      HumanReadableProposalStatus[
      proposalStatus as keyof typeof ProposalStatus
      ];
    const isClient = proposal.client?.userId.isEqualTo(userId);
    const isTeamLeader = !Masked.isMasked(proposal.vendorTeam)
      && proposal.vendorTeam?.leader?.userId.isEqualTo(userId);
    const isVendor = proposal.vendors?.some((vendor) =>
      vendor.userId.isEqualTo(userId)
    );

    if (
      (proposal.status === ProposalStatus.AwaitingApprovalByClient &&
        isClient) ||
      (proposal.status === ProposalStatus.AwaitingApprovalByTeamLeader &&
        isTeamLeader) ||
      (proposal.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
    )
      displayStatus = "Awaiting Your Approval";

    // If the proposal is awaiting review by the user
    if (
      proposal.clientReviewers.some((reviewer) =>
        session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved) ||
      proposal.vendorReviewers.some((reviewer) =>
        session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved)
    ) {
      displayStatus = "Awaiting Your Review";
    }

    return displayStatus;
  }

  function renderCreatorColumn(proposal: Proposal) {
    if (!proposal.creatorInfo) return null;
    if (proposal.creatorInfo.userId.isEqualTo(session.user?.id)) {
      return "You";
    }
    return <CreatorLink creator={proposal.creatorInfo} />;
  }

  function renderTeamColumn(proposal: Proposal) {
    if (!proposal.vendorTeam) return <Icon>remove</Icon>;
    return <VendorTeamLink proposal={proposal} session={session} />;
  }

  function renderFeeScheduleColumn(proposal: Proposal) {
    if (!proposal.feeSchedule) return <Icon>remove</Icon>;
    return <FeeScheduleLink proposal={proposal} />;
  }

  const columns = [
    columnHelper.accessor(
      renderStatusColumn,
      {
        header: "Status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: Object.entries(HumanReadableProposalStatus).map(
          ([key, value]) => ({ value: key, label: value })
        ),
      }
    ),
    columnHelper.accessor(
      (proposal) => proposal.name?.value,
      {
        header: "Name",
        id: "name",
        Cell: NameCell
      }
    ),
    columnHelper.accessor(
      (proposal) => proposal.description?.value,
      {
        header: "Description",
        id: "description",
      }
    ),
    columnHelper.accessor(
      renderCreatorColumn,
      {
        header: "Creator",
        id: "creator",
      }
    ),
    columnHelper.accessor(
      (proposal) => proposal.negotiable,
      {
        header: "Negotiable",
        id: "negotiable",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" }
        ],
        Cell: NegotiableCell,
      }
    ),
    columnHelper.accessor(
      renderTeamColumn,
      {
        header: "Team",
        id: "team",
        size: 50,
      }
    ),
    columnHelper.accessor(
      renderFeeScheduleColumn,
      {
        header: "Fee Schedule",
        id: "feeSchedule",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (proposal) =>
        proposal.responseDueBy?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>,
      {
        header: "Response Due By",
        id: "responseDueBy",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (proposal) =>
        proposal.startDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>,
      {
        header: "Start Date",
        id: "startDate",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (proposal) =>
        proposal.endDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>,
      {
        header: "End Date",
        id: "endDate",
        size: 50,
      }
    ),
  ];

  if (activeTab === PageTab.Archived) {
    const archivedColumns = [
      columnHelper.accessor(
        (proposal) => proposal.hireDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>,
        {
          header: "Hire Date",
          id: "hireDate",
          size: 50,
        }
      ),
      columnHelper.accessor(
        (proposal) => proposal.archiveDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>,
        {
          header: "Archive Date",
          id: "archiveDate",
          size: 50,
        }
      ),
    ];
    columns.push(...archivedColumns);
  }

  const getMenuItems = (proposal: Proposal): React.ReactNode[] => {
    const menuItems: React.ReactNode[] = [];

    if (
      !proposal.availableActions?.Edit.some((id) =>
        id.isEqualTo(session.user?.id)
      ) &&
      !proposal.availableActions?.Review.some((id) =>
        id.isEqualTo(session.user?.id)
      ) &&
      !proposal.availableActions?.Revise.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem
          key="view"
          onClick={() =>
            navigate(`/proposals/${activeTab}/view/${proposal.id}`)
          }
        >
          <ListItemIcon>
            <Icon>visibility</Icon>
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Edit.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem
          key="edit"
          onClick={() => navigate(`/proposals/drafts/edit/${proposal.id}`)}
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          <ListItemText primary="Review" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Review.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem
          key="review"
          onClick={() =>
            navigate(`/proposals/${activeTab}/review/${proposal.id}`)
          }
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Submit.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="submit" onClick={() => handleSubmitProposal(proposal)}>
          <ListItemIcon>
            <Icon>send</Icon>
          </ListItemIcon>
          <ListItemText primary="Submit" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Revise.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem
          key="revise"
          onClick={() => navigate(`/proposals/active/revise/${proposal.id}`)}
        >
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          <ListItemText primary="Revise" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Approve.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="approve" onClick={() => handleApproveProposal(proposal)}>
          <ListItemIcon>
            <Icon color="success">check</Icon>
          </ListItemIcon>
          <ListItemText primary="Approve" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Reject.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="reject" onClick={() => handleRejectProposal(proposal)}>
          <ListItemIcon>
            <Icon color="error">close</Icon>
          </ListItemIcon>
          <ListItemText primary="Reject" />
        </MenuItem>
      );
    }

    if (
      activeTab !== PageTab.Archived &&
      proposal.availableActions?.Hire?.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="hire" onClick={() => handleHireProposal(proposal)}>
          <ListItemIcon>
            <Icon>work</Icon>
          </ListItemIcon>
          <ListItemText primary="Hire" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Cancel.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="cancel" onClick={() => handleCancelProposal(proposal)}>
          <ListItemIcon>
            <Icon color="error">cancel</Icon>
          </ListItemIcon>
          <ListItemText primary="Cancel Negotiation" />
        </MenuItem>
      );
    }

    if (
      proposal.availableActions?.Delete.some((id) =>
        id.isEqualTo(session.user?.id)
      )
    ) {
      menuItems.push(
        <MenuItem key="delete" onClick={() => handleDeleteProposal(proposal)}>
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      );
    }

    return menuItems;
  };

  const openProposal = (proposal: Proposal) => {
    if (!proposal.id) {
      console.error("Proposal ID is required");
      return;
    }
    const editable = proposal.availableActions.Edit.some((userId) =>
      userId.isEqualTo(session.user?.id)
    );
    const revisable = proposal.availableActions.Revise.some((userId) =>
      userId.isEqualTo(session.user?.id)
    );
    const reviewable = proposal.availableActions.Review.some((userId) =>
      userId.isEqualTo(session.user?.id)
    );
    const submittable = proposal.availableActions.Submit.some((userId) =>
      userId.isEqualTo(session.user?.id)
    );

    let tab: PageTab = activeTab;
    let action: "view" | "edit" | "revise" | "review" = "view";

    if (editable && !revisable) {
      tab = PageTab.Drafts;
      action = "edit";
    }
    if (revisable) {
      if (submittable) {
        tab = PageTab.Drafts;
      } else {
        tab = PageTab.Active;
      }
      action = "revise";
    }
    if (reviewable) {
      action = "review";
    }

    navigate(`/proposals/${tab}/${action}/${proposal.id}`);
  };

  const table = useMaterialReactTable({
    columns,
    data: paginatedProposals?.data ?? [],
    enableRowSelection: activeTab === PageTab.Drafts,
    enableRowActions: true,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => openProposal(row.original),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ProposalToolbarButtonsContainer>
            <ProposalToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ProposalToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              onClick={() => {
                if (routeParams.action === "create") {
                  handleCreateDraftProposal();
                } else {
                  navigate("/proposals/drafts/create")
                }
              }}
            >
              Create Proposal
            </Button>
          </ProposalToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate("/proposals/drafts/create")}
          >
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <ProposalToolbar
          table={table}
          activeTab={activeTab}
          activeProposalCount={activeProposalCount}
          draftProposalCount={draftProposalCount}
          archivedProposalCount={archivedProposalCount}
        />
      );
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    renderRowActionMenuItems: ({ row }) => getMenuItems(row.original),
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: errorLoadingQuery
      ? {
        color: "error",
        children: "Error loading data",
      }
      : {
        action: (
          <ProposalAlertActions>
            <Button
              color="primary"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDraftProposalDelete}
            >
              Delete
            </Button>
          </ProposalAlertActions>
        ),
      },
    rowCount: rowCount,
    pageCount: Math.ceil((paginatedProposals?.totalElements ?? 0) / pagination.pageSize),
    enablePagination: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: Boolean(errorLoadingQuery),
      showProgressBars: isLoading,
      sorting,
      rowSelection,
    },
  });

  if (!session.user?.companyEntityId) {
    return <NotAuthorized />;
  }

  return <MaterialReactTable table={table} />;
}
